import { LotEventTypes, LotStatus } from '@parallel-fluidics/shared/constants';
import {
  formatLotCode,
  lotCounterFromFirestore,
  lotCounterToFirestore,
  lotDateKey,
  lotEventToFirestore,
  lotToFirestore,
} from '@parallel-fluidics/shared/models/lots';
import {
  collection,
  doc,
  getFirestore,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

/**
 * Create a lot with a unique daily code (L-YYYYMMDD-###). The code's bag
 * number comes from the lotCounters/{YYYYMMDD} doc, incremented in the same
 * transaction that writes the lot and its `created` event, so concurrent
 * creates can't collide. New lots always start Awaiting inspection.
 *
 * @returns {Promise<{ id: string, code: string }>} New lot doc ID and code.
 */
export default async function createLot({
  kind,
  projectDocId,
  pPartDocId,
  material = null,
  supplier = null,
  orderNumber = null,
  machine = null,
  aSideTool = null,
  bSideTool = null,
  initialQty,
  user,
}) {
  const db = getFirestore();
  const now = new Date();
  const counterRef = doc(db, 'lotCounters', lotDateKey(now));
  const lotRef = doc(collection(db, 'lots'));
  const eventRef = doc(collection(db, 'lots', lotRef.id, 'events'));

  const code = await runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);
    const count = (counterDoc.exists() ? lotCounterFromFirestore(counterDoc).count ?? 0 : 0) + 1;
    const lotCode = formatLotCode(now, count);

    transaction.set(counterRef, lotCounterToFirestore({ count }, serverTimestamp));
    transaction.set(lotRef, lotToFirestore({
      code: lotCode,
      kind,
      projectDocId,
      pPartDocId,
      material,
      supplier,
      orderNumber,
      machine,
      aSideTool,
      bSideTool,
      initialQty,
      remainingQty: initialQty,
      status: LotStatus.AWAITING_INSPECTION,
      sourceLotIds: [],
      _createdBy: user,
    }, serverTimestamp));
    transaction.set(eventRef, lotEventToFirestore({
      type: LotEventTypes.CREATED,
      deltaQty: initialQty,
      _createdBy: user,
    }, serverTimestamp));

    return lotCode;
  });

  return { id: lotRef.id, code };
}
